import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();

// User Portal Functions
export const exportUserData = async (): Promise<{
  exportId: string;
  recordCount: number;
}> => {
  const callable = httpsCallable(functions, 'exportUserData');
  
  try {
    const result = await callable({});
    return result.data as { exportId: string; recordCount: number };
  } catch (error) {
    console.error('Error calling exportUserData:', error);
    throw error;
  }
};

export const getUserStats = async (): Promise<{
  totalSubmissions: number;
  approvedSubmissions: number;
  rejectedSubmissions: number;
  pendingSubmissions: number;
  totalRewardEarned: number;
}> => {
  const callable = httpsCallable(functions, 'getUserStats');
  
  try {
    const result = await callable({});
    return result.data as {
      totalSubmissions: number;
      approvedSubmissions: number;
      rejectedSubmissions: number;
      pendingSubmissions: number;
      totalRewardEarned: number;
    };
  } catch (error) {
    console.error('Error calling getUserStats:', error);
    throw error;
  }
};

export const deleteUserAccount = async (): Promise<{
  success: boolean;
}> => {
  const callable = httpsCallable(functions, 'deleteUserAccount');
  
  try {
    const result = await callable({});
    return result.data as { success: boolean };
  } catch (error) {
    console.error('Error calling deleteUserAccount:', error);
    throw error;
  }
};

export const createDownloadPackage = async (submissionIds: string[]): Promise<{
  downloadUrl: string;
  totalLabels: number;
  totalPrice: number;
  exportId: string;
}> => {
  const callable = httpsCallable(functions, 'createDownloadPackage');
  
  try {
    const result = await callable({ submissionIds });
    return result.data as {
      downloadUrl: string;
      totalLabels: number;
      totalPrice: number;
      exportId: string;
    };
  } catch (error) {
    console.error('Error calling createDownloadPackage:', error);
    throw error;
  }
};

// Client Credits Functions
export interface ClientCredits {
  creditBalance: number;
  totalCreditsPurchased?: number;
  totalCreditsUsed?: number;
}

export interface CreditPackage {
  id: string;
  name: string;
  description?: string;
  credits: number;
  priceUsd: number;
  pixPrice?: number;
  pixDiscountPercent?: number;
  pixShow?: boolean;
}

export const getClientCredits = async (): Promise<ClientCredits> => {
  const callable = httpsCallable(functions, 'getClientCredits');

  try {
    const result = await callable({});
    return result.data as ClientCredits;
  } catch (error: any) {
    console.error('Error calling getClientCredits:', error);
    // If client profile hasn't been created yet, treat as zero credits
    if (error.code === 'not-found' || error.code === 'permission-denied') {
      return {
        creditBalance: 0,
        totalCreditsPurchased: 0,
        totalCreditsUsed: 0,
      };
    }
    throw error;
  }
};

export const listCreditPackages = async (): Promise<CreditPackage[]> => {
  const callable = httpsCallable(functions, 'listCreditPackages');

  try {
    const result = await callable({});
    return (result.data as CreditPackage[]) || [];
  } catch (error) {
    console.error('Error calling listCreditPackages:', error);
    return [];
  }
};

export const createCreditCheckoutSession = async (
  packageId: string,
  successUrl: string,
  cancelUrl: string
): Promise<{ url: string }> => {
  const callable = httpsCallable(functions, 'createCreditCheckoutSession');

  try {
    const result = await callable({
      packageId,
      successUrl,
      cancelUrl,
    });
    return result.data as { url: string };
  } catch (error) {
    console.error('Error calling createCreditCheckoutSession:', error);
    throw error;
  }
};

export interface PurchaseResult {
  purchased: number;
  creditsSpent: number;
  alreadyOwned: number;
  message: string;
}

export const purchaseMarketplaceNode = async (
  submissionIds: string[],
  type: 'rawPhoto' | 'annotatedPhoto' | 'rawVideo' = 'rawPhoto'
): Promise<PurchaseResult> => {
  const callable = httpsCallable(functions, 'purchaseMarketplaceNode');
  try {
    const result = await callable({ submissionIds, type });
    return result.data as PurchaseResult;
  } catch (error) {
    console.error('Error calling purchaseMarketplaceNode:', error);
    throw error;
  }
};

export interface ExportResult {
  success: boolean;
  exportId: string;
  downloadUrl: string;
  photoCount: number;
  skipped: number;
  status?: 'building' | 'ready' | 'failed';
}

export const exportOwnedRawPhotos = async (
  submissionIds: string[],
  type: 'rawPhoto' | 'annotatedPhoto' | 'rawVideo' = 'rawPhoto'
): Promise<ExportResult> => {
  const callable = httpsCallable(functions, 'exportOwnedRawPhotos');
  try {
    const result = await callable({ submissionIds, type });
    return result.data as ExportResult;
  } catch (error) {
    console.error('Error calling exportOwnedRawPhotos:', error);
    throw error;
  }
};

export interface ExportDownloadResult {
  success: boolean;
  exportId: string;
  downloadUrl: string;
  fileName?: string;
}

export const getExportDownloadUrl = async (
  exportId: string,
  fileName?: string
): Promise<ExportDownloadResult> => {
  const callable = httpsCallable(functions, 'getExportDownloadUrl');
  try {
    const payload: { exportId: string; fileName?: string } = { exportId };
    if (fileName) payload.fileName = fileName;
    const result = await callable(payload);
    return result.data as ExportDownloadResult;
  } catch (error) {
    console.error('Error calling getExportDownloadUrl:', error);
    throw error;
  }
};

export type MarketplaceItem = {
  id: string;
  category: string;
  subcategory: string;
  itemType: string;
  taskTitle: string;
  taskId: string;
  credits: number;
  releasedAt: string | null;
  imageUrl: string;
  videoUrl?: string;
  durationSeconds?: number;
  labelClassIds?: string[];
  labelCount?: number;
};

export interface ListMarketplaceResult {
  items: MarketplaceItem[];
  type: string;
}

export const listMarketplace = async (
  type: 'rawPhoto' | 'annotatedPhoto' | 'rawVideo'
): Promise<ListMarketplaceResult> => {
  const callable = httpsCallable(functions, 'listMarketplace');
  try {
    const result = await callable({ type });
    return result.data as ListMarketplaceResult;
  } catch (error) {
    console.error('Error calling listMarketplace:', error);
    throw error;
  }
};

export type OwnedSubmissionItem = {
  submissionId: string;
  type: string;
  purchasedAt: string | null;
  creditsPaid: number;
  lastExportedAt: string | null;
  exportCount: number;
  labelCount: number;
  labelClassIds: string[];
  category: string;
  subcategory: string;
  itemType: string;
  taskTitle: string;
  taskId: string;
  imageUrl: string;
  videoUrl?: string;
  durationSeconds?: number;
};

export interface ListOwnedSubmissionsResult {
  items: OwnedSubmissionItem[];
}

export const listOwnedSubmissions = async (): Promise<ListOwnedSubmissionsResult> => {
  const callable = httpsCallable(functions, 'listOwnedSubmissions');
  try {
    const result = await callable({});
    return result.data as ListOwnedSubmissionsResult;
  } catch (error) {
    console.error('Error calling listOwnedSubmissions:', error);
    throw error;
  }
};

export interface RegisterCustomerInput {
  name: string;
  company: string;
  role: string;
  workingOn: string;
  dataTypes: string[];
  dataTypesOther: string;
  timeline: string;
  website: string;
  companySize: string;
  heardAbout: string;
  wantsConsult: boolean;
  hadContributorAccount: boolean;
}

export const registerCustomer = async (
  input: RegisterCustomerInput
): Promise<{ ok: boolean }> => {
  const callable = httpsCallable(functions, 'registerCustomer');
  try {
    const result = await callable(input);
    return result.data as { ok: boolean };
  } catch (error) {
    console.error('Error calling registerCustomer:', error);
    throw error;
  }
};

export type CreditPurchaseItem = {
  id: string;
  packageId: string;
  credits: number;
  amountUsd: number;
  purchasedAt: string | null;
};

export interface ListCreditPurchasesResult {
  items: CreditPurchaseItem[];
}

export const listCreditPurchases = async (): Promise<ListCreditPurchasesResult> => {
  const callable = httpsCallable(functions, 'listCreditPurchases');
  try {
    const result = await callable({});
    return result.data as ListCreditPurchasesResult;
  } catch (error) {
    console.error('Error calling listCreditPurchases:', error);
    throw error;
  }
};

export type CreditUsageGroup = {
  category: string;
  subcategory: string;
  taskTitle: string;
  itemType: string;
  count: number;
  credits: number;
};

export type CreditUsageItem = {
  id: string;
  type: string;
  totalItems: number;
  totalCredits: number;
  spentAt: string | null;
  groups: CreditUsageGroup[];
};

export interface ListCreditUsageResult {
  items: CreditUsageItem[];
}

export const listCreditUsage = async (): Promise<ListCreditUsageResult> => {
  const callable = httpsCallable(functions, 'listCreditUsage');
  try {
    const result = await callable({});
    return result.data as ListCreditUsageResult;
  } catch (error) {
    console.error('Error calling listCreditUsage:', error);
    throw error;
  }
};

